import { useState, useEffect, useRef } from 'react';
import { motion } from 'motion/react';
import { useAuthStore } from '@/store/authStore';
import {
  startOAuthLogin,
  handleAuthCallback,
  getZkProof,
  getAuthData,
  isAuthenticated,
  type OAuthProviderId,
} from '@/services/zklogin-auth';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import {
  stashPostLoginDest,
  peekPostLoginDest,
  takePostLoginDest,
  resolvePostLoginDest,
} from '@/lib/post-login-dest';

// Apple sign-in ships dark: the whole point is customers who have no Google
// account, and until the Services ID and .p8 key are configured in production
// the button can only fail. Flipping this on is the last step of rollout.
const APPLE_ENABLED = Boolean(import.meta.env.VITE_APPLE_LOGIN_ENABLED);

/**
 * The destination to land on after login.
 *
 * localStorage is the primary carrier and is what Google has always used. The
 * ?dest fallback exists for Apple: its callback is a cross-site POST handled by
 * our server, which forwards the HMAC-verified destination as a query param. It
 * is the backup for the case where localStorage did not survive the round trip
 * (Safari ITP, private browsing). Both paths run through resolvePostLoginDest,
 * so a tampered value degrades to '/' rather than becoming an open redirect.
 */
function resolveFinalDest(now: number): string {
  const stashed = takePostLoginDest(now);
  if (stashed !== '/') return stashed;
  const fromUrl = new URLSearchParams(window.location.search).get('dest');
  if (!fromUrl) return '/';
  return resolvePostLoginDest(JSON.stringify({ path: fromUrl, ts: now }), now);
}

function OAuthLogin() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState('');
  const { setAuth } = useAuthStore();
  const navigate = useNavigate();
  const location = useLocation();
  // Which copy variant to show.
  //
  // Pre-OAuth this must come from location.pathname, NOT from the stash: the
  // capture effect below has not run yet on first render, so peeking an empty
  // store would return '/' and paint one frame of the default consumer copy
  // before correcting itself — a visible flash of the wrong branding on
  // exactly the journey this variant exists to fix. The pathname is already
  // known synchronously, so use it.
  //
  // On the callback render the pathname is /auth/callback and the original
  // destination is long gone, so there the stash IS the only source.
  const callbackParams = new URLSearchParams(window.location.search);
  const isCallbackRender = callbackParams.has('code') || callbackParams.has('error');
  const [intendedDest] = useState(() =>
    isCallbackRender ? peekPostLoginDest(Date.now()) : location.pathname,
  );
  // OAuth codes are single-use: never exchange twice (StrictMode double-mount,
  // location-effect re-fires) — a replay always fails with invalid_grant.
  const callbackStarted = useRef(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.has('code') || params.has('error')) {
      if (callbackStarted.current) return;
      callbackStarted.current = true;
      // Stale ?code with a live session (refresh/back-button onto the callback
      // URL after login already completed) — don't re-exchange, just restore.
      if (params.has('code') && isAuthenticated()) {
        const authData = getAuthData();
        if (authData.address) {
          setAuth(authData.jwt || '', authData.address, null, authData.proof || '');
        }
        const dest = resolveFinalDest(Date.now());
        window.history.replaceState(null, '', dest);
        navigate(dest, { replace: true });
        return;
      }
      processCallback();
    } else {
      // Pre-OAuth render. App.tsx swaps this component in *for* the requested
      // route, so location.pathname is still the intended destination —
      // capture it before startOAuthLogin() navigates away to the provider.
      const here = location.pathname + location.search;
      stashPostLoginDest(here, Date.now());

      if (isAuthenticated()) {
        // Session exists but Zustand lost it (e.g. HMR). Restore without redirecting
        // so deep-link URLs like /shops/owner/dashboard are preserved.
        const authData = getAuthData();
        if (authData.address) {
          setAuth(authData.jwt || '', authData.address, null, authData.proof || '');
        }
      }
    }
  }, [location]);

  async function processCallback() {
    setLoading(true);
    setError(null);

    try {
      setStatus('EXCHANGING AUTH CODE...');
      const result = await handleAuthCallback();

      setStatus('GENERATING ZK PROOF...');
      const zkProof = await getZkProof(result.jwt, result.salt);
      const proof = JSON.stringify(zkProof);

      setStatus('WELCOME! REDIRECTING...');
      setAuth(result.jwt, result.address, null, proof);

      const dest = resolveFinalDest(Date.now());
      // An Apple signup whose email already has a Google card gets a one-time
      // heads-up. The two cards CANNOT be merged — the wallet address is
      // derived from the provider's issuer, so they are different wallets by
      // construction — so this is the only chance to stop someone concluding
      // the app lost their stamps.
      const withNotice = result.existingProvider
        ? `${dest}${dest.includes('?') ? '&' : '?'}existing=${encodeURIComponent(result.existingProvider)}`
        : dest;
      window.history.replaceState(null, '', withNotice);
      navigate(withNotice);
    } catch (err: any) {
      console.error('Auth callback error:', err);
      setError(err.message || 'Authentication failed');
      // Strip the consumed ?code so a refresh gives a clean login page
      // instead of replaying the dead code into another invalid_grant.
      window.history.replaceState(null, '', '/auth/callback');
    } finally {
      setLoading(false);
    }
  }

  async function handleLogin(provider: OAuthProviderId) {
    setLoading(true);
    setError(null);
    setStatus(provider === 'apple' ? 'CONNECTING TO APPLE...' : 'CONNECTING TO GOOGLE...');

    try {
      // Pass the destination explicitly for Apple: it is signed into the OAuth
      // `state` server-side so it survives the cross-site POST even when
      // localStorage does not.
      await startOAuthLogin(provider, location.pathname + location.search);
    } catch (err: any) {
      console.error('Login error:', err);
      setError(err.message || 'Failed to start login');
      setLoading(false);
    }
  }

  return (
    <div className="min-h-dvh bg-coral relative overflow-hidden flex flex-col items-center px-7 pt-[50px] pb-10 safe-shell">
      {/* Gold circle, top-right */}
      <div className="absolute -top-[70px] -right-[70px] w-[250px] h-[250px] rounded-full bg-accent-alt pointer-events-none" />
      {/* Translucent teal circle, bottom-left */}
      <div className="absolute -bottom-[90px] -left-[60px] w-[200px] h-[200px] rounded-full bg-pixel-cyan/35 pointer-events-none" />

      {/* THE ORIGINAL chip */}
      <div className="relative rounded-full bg-black/[.18] font-heading text-[8px] text-white tracking-[1px] px-4 py-2">
        THE ORIGINAL
      </div>

      <div className="relative flex-1 w-full max-w-sm flex flex-col items-center justify-center gap-3 text-center">
        {/* Title. Shop-owner variant when the user is mid-registration —
            a café owner who clicked a business CTA should not be answered
            by a consumer login wall with no sign they're still in the
            right flow. */}
        {intendedDest === '/shops/register' ? (
          <>
            <h1 className="font-heading text-xl md:text-2xl text-white leading-relaxed">
              REGISTER YOUR SHOP
            </h1>
            <p className="font-grotesk font-medium text-base text-white/90">
              Sign in to continue
            </p>
          </>
        ) : (
          <>
            <h1 className="font-heading text-3xl md:text-4xl text-white leading-relaxed tracking-wide">
              BIT
              <br />
              BREW
            </h1>
            <p className="font-grotesk font-medium text-base text-white/90">
              Coffee loyalty rewards
            </p>
          </>
        )}

        {/* Pixel mug — same coral-mug construction as BrewStation's idle
            cup (border + offset shadow, teal collar, gold/black face), but
            fixed to the mockup's black-on-white sign-in treatment rather
            than the shop-tinted live/waiting palette. */}
        <div className="relative w-[150px] h-[112px] mt-[26px]">
          <motion.div
            animate={{ y: [0, -24, 0], opacity: [0, 1, 0] }}
            transition={{ repeat: Infinity, duration: 2, ease: 'linear' }}
            className="absolute -top-6 left-6 w-3 h-6 bg-white/50"
          />
          <motion.div
            animate={{ y: [0, -30, 0], opacity: [0, 1, 0] }}
            transition={{ repeat: Infinity, duration: 2.5, delay: 0.5, ease: 'linear' }}
            className="absolute -top-[34px] left-12 w-3 h-[30px] bg-white/50"
          />
          <div className="absolute top-0 left-0 w-24 h-24 rounded-[8px] border-[5px] border-primary bg-white shadow-pixel-lg">
            <div className="absolute top-0 left-0 right-0 h-3 border-b-[5px] border-primary bg-pixel-cyan" />
            <div className="absolute top-6 left-3 w-3 h-3 bg-accent-alt" />
            <div className="absolute top-12 left-3 w-3 h-6 bg-accent-alt" />
            <div className="absolute top-9 left-6 w-3 h-3 bg-primary" />
            <div className="absolute top-9 right-6 w-3 h-3 bg-primary" />
            <div className="absolute top-[60px] left-9 w-6 h-3 bg-primary" />
            <div className="absolute top-3 -right-9 w-9 h-12 rounded-r-[8px] border-[5px] border-l-0 border-primary shadow-pixel-lg" />
          </div>
        </div>
      </div>

      {/* Status Message */}
      {status && loading && (
        <div className="relative w-full max-w-sm mb-4 rounded-[12px] bg-black/[.18] p-4 text-center">
          <p className="font-grotesk font-bold text-[13px] text-white">{status}</p>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="relative w-full max-w-sm mb-4 rounded-[12px] bg-black/[.18] p-4 text-center">
          <p className="font-grotesk font-bold text-[13px] text-white mb-1">Something went wrong</p>
          <p className="font-grotesk font-medium text-[12px] text-white/80">{error}</p>
        </div>
      )}

      {/* Login Buttons */}
      {!loading && (
        <div className="relative w-full max-w-sm flex flex-col gap-3">
          <button
            onClick={() => handleLogin('google')}
            disabled={loading}
            className="w-full rounded-[12px] bg-white text-primary font-grotesk font-bold text-[15px] p-[18px] flex items-center justify-center gap-2.5 disabled:opacity-50"
          >
            {/* Google icon */}
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" />
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
            </svg>
            Sign in with Google
          </button>

          {APPLE_ENABLED && (
            <button
              onClick={() => handleLogin('apple')}
              disabled={loading}
              className="w-full rounded-[12px] bg-white text-primary font-grotesk font-bold text-[15px] p-[18px] flex items-center justify-center gap-2.5 disabled:opacity-50"
            >
              {/* Apple icon */}
              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                <path d="M17.05 12.54c-.03-2.66 2.17-3.94 2.27-4-1.24-1.81-3.17-2.06-3.85-2.09-1.64-.17-3.2.96-4.03.96-.83 0-2.11-.94-3.47-.91-1.79.03-3.44 1.04-4.36 2.64-1.86 3.23-.48 8 1.33 10.61.88 1.28 1.94 2.71 3.32 2.66 1.33-.05 1.84-.86 3.45-.86 1.61 0 2.06.86 3.47.83 1.43-.02 2.34-1.3 3.22-2.59 1.01-1.48 1.43-2.92 1.45-3-.03-.01-2.78-1.07-2.8-4.25z" />
                <path d="M14.42 4.6c.73-.89 1.22-2.12 1.09-3.35-1.05.04-2.32.7-3.08 1.58-.68.78-1.27 2.03-1.11 3.23 1.17.09 2.37-.6 3.1-1.46z" />
              </svg>
              Sign in with Apple
            </button>
          )}
        </div>
      )}

      <p className="relative font-grotesk font-medium text-[13px] text-white/75 mt-3.5 text-center">
        No password. No download.
      </p>

      <p className="relative font-grotesk font-medium text-[11px] text-white/60 mt-5 text-center max-w-sm">
        By signing in you'll be asked to accept our{' '}
        <Link to="/terms" className="text-white underline">Terms of Service</Link>{' '}
        and{' '}
        <Link to="/privacy" className="text-white underline">Privacy Policy</Link>.
      </p>
    </div>
  );
}

export default OAuthLogin;
